import React, { useState, useContext, useEffect } from 'react';
import { navigate } from 'gatsby';
import useFirebase from '../hooks/useFirebase';

import ApiFetch from '../utils/api';

const AuthContext = React.createContext();

export const useAuth = () => useContext(AuthContext);

/**
 * Login redirect. Runs inside onAuthStateChanged immediately after the
 * loginCheck response is received, so it covers every authentication path
 * (sign-in, signup, password reset) — not just Login.js. When it navigates to
 * /pay-now it wins over Login.js's synchronous navigate('/dashboard') because it
 * fires after the async loginCheck; the brief /dashboard mount is masked by the
 * loading state. Gated by GATSBY_PAY_NOW_REDIRECT_ENABLED for safe rollback.
 */
const maybeRedirectToPayNow = (shopData) => {
  if (process.env.GATSBY_PAY_NOW_REDIRECT_ENABLED !== 'true') {
    return;
  }
  if (typeof window === 'undefined') {
    return;
  }
  // Already on /pay-now — let the page own its own state, don't re-navigate.
  if (window.location.pathname.startsWith('/pay-now')) {
    return;
  }

  // Dunning bypass: members arriving from a dunning email (?from=dunning) go
  // straight to /pay-now without classification — the page re-validates state.
  const params = new URLSearchParams(window.location.search);
  if (params.get('from') === 'dunning') {
    navigate('/pay-now');
    return;
  }

  const subscriptions = shopData?.payments?.subscriptions;
  if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
    return;
  }

  const hasActive = subscriptions.some((s) => s.status === 'Active');
  const hasPastDue = subscriptions.some((s) => s.status === 'Past Due');
  if (hasPastDue && !hasActive) {
    navigate('/pay-now');
  }
};

export const AuthProvider = ({ children }) => {
  const firebase = useFirebase();

  useEffect(() => {
    if (!firebase) {
      return;
    }
  }, [firebase]);

  const [currentUser, setCurrentUser] = useState(undefined);
  const [loading, setLoading] = useState(true);

  // Set when a Firebase-authenticated user's loginCheck does not return account
  // data — e.g. the account has no matching record in our database. Lets the UI
  // distinguish a broken account (tell them to call in) from a signed-out
  // visitor (send them to sign-in).
  const [authError, setAuthError] = useState(false);

  // Bumped to force a provider re-render after refreshShopData mutates the
  // reference-stable Firebase user object's shopData in place (setCurrentUser
  // with the same object reference would no-op).
  const [, setShopDataTick] = useState(0);

  const signup = (email, password) => firebase.auth().createUserWithEmailAndPassword(email, password);
  const login = (email, password) => firebase.auth().signInWithEmailAndPassword(email, password);
  const logout = () => firebase.auth().signOut();
  const resetPassword = (email) => firebase.auth().sendPasswordResetEmail(email);

  const updateEmail = async (email, password) => {
    const user = firebase.auth().currentUser;
    const credential = firebase.firebase_.auth.EmailAuthProvider.credential(user.email, password);

    try {
      await user.reauthenticateWithCredential(credential);
      // User re-authenticated.
      return user.updateEmail(email);
    } catch (error) {
      console.error('error: ', error);
      return error;
    }
  };

  const updatePassword = (password) => currentUser.updatePassword(password);

  useEffect(() => {
    setLoading(true);

    if (!firebase) {
      return;
    }

    return firebase.auth().onAuthStateChanged(async (user) => {
      setLoading(true);

      try {
        // Signed out — commit the null user and stop here.
        if (!user) {
          setAuthError(false);
          setCurrentUser(user);
          return;
        }

        setCurrentUser(user);

        const idToken = await firebase.auth().currentUser.getIdToken(true);
        const loginCheckRequest = {
          method: 'POST',
          path: `/auth/loginCheck`,
          body: { idToken },
        };
        const result = await ApiFetch(loginCheckRequest);

        if (result && result.shopData) {
          result.shopData.idToken = idToken;
          user.shopData = result.shopData;
          setAuthError(false);
          // Route lapsed (Past Due) members to /pay-now.
          maybeRedirectToPayNow(result.shopData);
        } else {
          // Authenticated with Firebase but loginCheck returned no account data
          // (the API responds with an error body, not shopData). Mark the
          // session as failed AND flag the account problem so the UI prompts the
          // member to call in instead of showing the generic registration prompt.
          setCurrentUser(false);
          setAuthError(true);
        }
      } catch (error) {
        console.log('error: ', error);
        // Leave currentUser as false to signal the failed session — don't
        // fall through to overwrite it with the user object.
        setCurrentUser(false);
        setAuthError(true);
      } finally {
        setLoading(false);
      }
    });
  }, [firebase]);

  const updateDisplayName = (name) => {
    if (currentUser) {
      currentUser
        .updateProfile({ displayName: name })
        .catch(function (error) {
          console.log('error', error);
        });
    }
  };

  const updateUser = (user) => setCurrentUser(user);

  // Re-run loginCheck and refresh the cached shopData (subscriptions,
  // payment methods) WITHOUT a logout/login. Called after a self-service payment
  // so the restored Active subscription is reflected immediately — otherwise the
  // session keeps the Past Due snapshot fetched at login. Force-refreshes the ID
  // token so any updated custom claims are picked up too. Returns the fresh
  // shopData, or null if there is no signed-in user / the call fails.
  const refreshShopData = async () => {
    const user = firebase && firebase.auth().currentUser;

    if (!user) {
      return null;
    }

    try {
      const idToken = await user.getIdToken(true);
      const loginCheckRequest = {
        method: 'POST',
        path: `/auth/loginCheck`,
        body: { idToken },
      };
      const result = await ApiFetch(loginCheckRequest);

      if (result && result.shopData) {
        result.shopData.idToken = idToken;
        user.shopData = result.shopData;
        setShopDataTick((t) => t + 1);
        return result.shopData;
      }
    } catch (error) {
      console.log('refreshShopData error: ', error);
    }

    return null;
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loading,
        authError,
        signup,
        login,
        logout,
        resetPassword,
        updateEmail,
        updatePassword,
        updateDisplayName,
        updateUser,
        refreshShopData,
        firebase,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
